import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Facebook, Menu, X, Info } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const GOLD = "#B8901F";
const CREAM = "#FAF7F0";
const BLACK = "#161412";
const NAME = "Tooth Doctors Dental Clinic";
const FB = "https://www.facebook.com/toothdoctorsdavao/";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-70px" }, transition: { duration: 0.6, ease: "easeOut" } };

export default function ToothDoctorsConcept() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Diagnostics", "#diagnostics"], ["Results", "#results"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: "#221D14", fontFamily: "'Jost', sans-serif" }} data-testid="toothdoctors-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Italiana&family=Jost:wght@400;500;600&display=swap'); .td-serif{font-family:'Italiana',serif;}`}</style>

      <div data-testid="dental-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-[#FAF7F0]/95 backdrop-blur border-b" style={{ borderColor: "#E8DEC6" }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="td-logo" className="flex items-center gap-3">
            <img src="/dental/tooth-doctors-dental-concept/logo.jpg" alt={NAME} className="h-10 w-10 object-contain" />
            <span className="td-serif text-2xl">Tooth Doctors</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm text-[#4A4030] hover:text-black transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="td-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5" style={{ background: GOLD }}>Visit Us</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="td-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-[#FAF7F0]">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — split, live text + the clinic's own branded photo */}
      <section className="grid md:grid-cols-2 min-h-[82vh]" data-testid="td-hero">
        <div className="flex flex-col justify-center px-8 sm:px-14 py-16">
          <motion.div {...fadeUp}>
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: GOLD }}>Matina, Davao City</span>
            <h1 className="mt-6 td-serif text-5xl sm:text-6xl leading-[1.1]">We bring smiles to life.</h1>
            <p className="mt-6 text-[#4A4030]/80 max-w-md">Cosmetic, orthodontic and general dentistry with in-house X-ray, fronting Mapúa Malayan Colleges Mindanao.</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#visit" data-testid="td-hero-cta" className="px-7 py-3.5 text-sm font-medium text-white" style={{ background: GOLD }}>Visit Us</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="td-hero-contact" className="px-7 py-3.5 text-sm font-medium border-2" style={{ borderColor: GOLD, color: "#221D14" }}>Contact Us</a>
            </div>
            <p className="mt-8 text-sm text-[#4A4030]/60">4.8★ Rating · 54 Reviews</p>
          </motion.div>
        </div>
        <motion.div {...fadeUp} className="relative min-h-[320px] overflow-hidden">
          <img src="/dental/tooth-doctors-dental-concept/hero.jpg" alt="A patient's smile, from the Tooth Doctors Dental Clinic brand photography" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "0% center" }} />
        </motion.div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl td-serif leading-relaxed">
          Tooth Doctors Dental Clinic offers cosmetic, orthodontic and general dentistry — with in-house X-ray so patients rarely need to go elsewhere for diagnostics.
        </motion.p>
      </section>

      {/* DIAGNOSTICS — the clinic's own real X-ray comparisons */}
      <section id="diagnostics" className="py-24" style={{ background: BLACK }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: GOLD }}>Real Diagnostics</span>
            <h2 className="mt-3 text-4xl td-serif text-white">Seeing the full picture</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            <motion.img {...fadeUp} src="/dental/tooth-doctors-dental-concept/ortho-xray1.jpg" alt="Orthodontic X-ray comparison shared by Tooth Doctors" className="w-full object-cover" />
            <motion.img {...fadeUp} src="/dental/tooth-doctors-dental-concept/ortho-xray2.jpg" alt="Orthodontic progress detail shared by Tooth Doctors" className="w-full object-cover" />
          </div>
        </div>
      </section>

      {/* RESULT */}
      <section id="results" className="max-w-4xl mx-auto px-5 sm:px-8 py-24 text-center">
        <motion.h2 {...fadeUp} className="td-serif text-3xl mb-10">A Real Patient Result</motion.h2>
        <motion.img {...fadeUp} src="/dental/tooth-doctors-dental-concept/results.jpg" alt="A real patient result shared by Tooth Doctors (@toothdoctorsdavao)" className="w-full max-w-sm mx-auto object-contain shadow-lg" />
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" className="py-24" style={{ background: "#F1E8D0" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="td-serif text-3xl text-center mb-14">What Patients Say</motion.h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { text: "My partner and I were both treated here — they explain what will be done, the dentists and staff are kind, and they have their own in-house X-ray.", author: "WillowHefty", source: "Reddit" },
              { text: "I'd put my own integrity behind Tooth Doctors — strongly recommend them.", author: "Vee Osico", source: "Facebook" },
              { text: "A public reel strongly recommends Tooth Doctors and calls it the best.", author: "Bella Crawford", source: "Instagram" },
            ].map((q, i) => (
              <motion.div key={i} {...fadeUp} data-testid={`td-testimonial-${i}`} className="p-7 bg-white shadow-sm">
                <p className="text-[#3A3226]/85 leading-relaxed">"{q.text}"</p>
                <p className="mt-4 text-sm font-semibold" style={{ color: GOLD }}>{q.author} <span className="text-[#8A7D5C] font-normal">· {q.source}</span></p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-24" style={{ background: BLACK }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="td-serif text-4xl text-white">Visit Us</h2>
            <ul className="mt-8 space-y-5 text-white/70">
              <li className="flex items-start gap-4"><MapPin size={20} style={{ color: GOLD }} className="mt-0.5" />2nd Floor Saito Bldg., McArthur Highway, Matina, Davao City (fronting Mapúa Malayan Colleges Mindanao)</li>
              <li className="flex items-start gap-4"><Phone size={20} style={{ color: GOLD }} className="mt-0.5" /><a href="tel:+639189288143" className="hover:underline">+63 918 928 8143</a> · (082) 286-2331</li>
              <li className="flex items-start gap-4"><Mail size={20} style={{ color: GOLD }} className="mt-0.5" /><a href="mailto:toothdoctorsdvo@gmail.com" className="hover:underline">toothdoctorsdvo@gmail.com</a></li>
              <li className="flex items-start gap-4"><Facebook size={20} style={{ color: GOLD }} className="mt-0.5" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="td-facebook-link" className="hover:underline">toothdoctorsdavao</a></li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[320px]">
            <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("Saito Building McArthur Highway Matina Davao City")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </motion.div>
        </div>
      </section>

      <footer className="py-10 bg-[#FAF7F0] border-t" style={{ borderColor: "#E8DEC6" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#6B6350]">
          <p className="td-serif text-base">Tooth Doctors Dental Clinic</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-medium text-white shadow-xl" style={{ background: GOLD }}>Message Tooth Doctors</a>
      <ConceptCTAPill slug="tooth-doctors-dental-concept" />
    </div>
  );
}
